import { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import { calculateListofDocumentTypes, evaluateDocTypeRules, fetchDocTypeRules } from "../util/docTypeRuleEvaluator";

const useDocTypeRules = ({ formType, applicationData, enabled = true } = {}) => {
  const [docTypes, setDocTypes] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const latestApplicationData = useRef(applicationData);
  const previousApplicationData = useRef(_.cloneDeep(applicationData));

  const setDocTypesIfChanged = useCallback((nextDocTypes) => {
    const resolvedDocTypes = Array.isArray(nextDocTypes) ? nextDocTypes : [];
    setDocTypes((currentDocTypes) => (_.isEqual(currentDocTypes, resolvedDocTypes) ? currentDocTypes : resolvedDocTypes));
  }, []);

  const setRulesIfChanged = useCallback((nextRules) => {
    const resolvedRules = Array.isArray(nextRules) ? nextRules : [];
    setRules((currentRules) => (_.isEqual(currentRules, resolvedRules) ? currentRules : resolvedRules));
  }, []);

  useEffect(() => {
    latestApplicationData.current = applicationData;
  }, [applicationData]);

  const evaluateRules = useCallback(
    (rulesToEvaluate) => {
      try {
        if (!rulesToEvaluate?.length) {
          setDocTypesIfChanged([]);
          return;
        }
        const result = evaluateDocTypeRules(rulesToEvaluate, latestApplicationData.current);
        setDocTypesIfChanged(result || []);
      } catch (err) {
        setError(err);
        setDocTypesIfChanged([]);
      }
    },
    [setDocTypesIfChanged],
  );

  const loadRules = useCallback(
    async (opts = {}) => {
      if (!enabled || !formType) {
        setDocTypesIfChanged([]);
        setRulesIfChanged([]);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const fetchedRules = await fetchDocTypeRules(formType, opts);
        setRulesIfChanged(fetchedRules);
        evaluateRules(fetchedRules);
      } catch (err) {
        setError(err);
        setDocTypesIfChanged([]);
      } finally {
        setLoading(false);
      }
    },
    [enabled, evaluateRules, formType, setDocTypesIfChanged, setRulesIfChanged],
  );

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  useEffect(() => {
    latestApplicationData.current = applicationData;
    if (!rules?.length || !enabled) {
      previousApplicationData.current = _.cloneDeep(applicationData);
      return;
    }
    // Some callers build `applicationData` inline, so guard against identity-only changes.
    if (_.isEqual(previousApplicationData.current, applicationData)) {
      return;
    }
    previousApplicationData.current = _.cloneDeep(applicationData);
    evaluateRules(rules);
  }, [enabled, evaluateRules, rules, applicationData]);

  const refetch = useCallback(() => loadRules({ force: true }), [loadRules]);
  const evaluateWithLatest = useCallback(
    async ({ dataOverride } = {}) => {
      try {
        const baseData = dataOverride ?? latestApplicationData.current;
        const result = await calculateListofDocumentTypes({ formType, applicationData: baseData, force: true });
        setDocTypesIfChanged(result || []);
        return result || [];
      } catch (err) {
        setError(err);
        setDocTypesIfChanged([]);
        return [];
      }
    },
    [formType, setDocTypesIfChanged],
  );

  return {
    docTypes,
    rules,
    loading,
    error,
    refetch,
    evaluateWithLatest,
  };
};

export default useDocTypeRules;
